<script setup lang="ts">
import { computed, watch } from 'vue';
import { useClientHandle } from '@urql/vue';
import type { RouteLocationRaw } from 'vue-router';

import { verifyRoute, verifyRouteResponse } from '../../router/verifyRoute';
import { dashboard, login, type pageMetaTypes } from '../../config/pages';
import { getConfig } from '../../config/constants';
import useSaveSesionData from '../../hooks/useSaveSesionData';
import { getRedirectPath } from '../../util/getRedirectPath';
import {
  type PortalContactLoggedQuery,
  PortalContactLoggedDocument,
} from '../../graphql/pp/generated/graphql';

const route = useRoute();
const constants = getConfig(useRuntimeConfig());
const { onSave } = useSaveSesionData();
const { partner } = usePartner();

const { data, fetching } = useClientHandle().useQuery<PortalContactLoggedQuery>({
  query: PortalContactLoggedDocument,
  requestPolicy: 'network-only',
});

const user = computed(() => data.value?.portalContactLogged);
const isLogged = computed(() => !!user.value);

watch(user, (value) => {
  if (!value) {
    return;
  }
  try {
    const { uuid } = route.params;
    onSave(value, typeof uuid === 'string' ? uuid : undefined);
  } catch {
    // A logged contact without an active partner has no dashboard to land on.
    // Mirror the legacy middleware which swallowed this and let the page render
    // its own "no rights" state.
  }
}, { immediate: true });

const result = computed(() => verifyRoute(route.meta.type as pageMetaTypes | undefined, isLogged.value));

const loginTarget = computed<RouteLocationRaw>(() => {
  if (window.location.pathname === dashboard) {
    return { path: login };
  }
  return { path: login, query: { redirect: btoa(window.location.href) } };
});
const dashboardTarget = computed(() => {
  const { redirect } = route.query;
  if (typeof redirect === 'string' && redirect.length > 0) {
    return getRedirectPath(constants.appURL, redirect);
  }
  const partnerUuid = partner.value?.partner.uuid
    ?? user.value?.partnerPortalContactsActive[0]?.partner.uuid;
  return `/${partnerUuid}${dashboard}`;
});
</script>

<template>
  <SessionSkeleton v-if="fetching" />
  <SessionRedirect
    v-else-if="result === verifyRouteResponse.login"
    :to="loginTarget"
    message="Redirecting to login..."
  />
  <SessionRedirect
    v-else-if="result === verifyRouteResponse.dashboard"
    :to="dashboardTarget"
    replace
    message="Redirecting to your dashboard..."
  />
  <slot v-else />
</template>
